import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, Copy, KeyRound, Loader2, RotateCw, Shield } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FirewallSection } from "@/components/FirewallSection";

type Env = "production" | "staging" | "preview" | "development";
type Level = "none" | "password" | "trusted_ips" | "oidc" | "platform_auth";

interface EnvState {
  protectionLevel: Level;
  hasPassword: boolean;
  hasBypassSecret: boolean;
  trustedIps: string[];
  passwordSetAt: string | null;
}

interface ProtectionResponse {
  envs: Record<Env, EnvState>;
  optionsAllowlist: string[];
}

const ENVS: Env[] = ["production", "staging", "preview", "development"];

/**
 * T031 + T052 (spec 002 US3 + US8) — Project protection settings.
 *
 * Env-tabbed UI: per env, choose protection level (none / password /
 * trusted_ips), rotate password, edit trusted-IP CIDRs (US8), and
 * regenerate the automation bypass secret (T055). The OPTIONS allowlist
 * (T034b) lives below the env tabs, project-wide.
 */
export function ProjectProtection() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [activeEnv, setActiveEnv] = useState<Env>("production");

  const q = useQuery({
    queryKey: ["protection", org, project],
    queryFn: () =>
      api.get<ProtectionResponse>(`/orgs/${org}/projects/${project}/protection`),
  });

  if (q.isLoading || !q.data) {
    return <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>;
  }
  const state = q.data;
  const envState = state.envs[activeEnv];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold inline-flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" /> Protection
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Gate live deployments behind a password, a list of trusted IPs, or leave them open.
          Settings apply per-env.
        </p>
      </div>

      <div className="of-card">
        <nav className="of-tabstrip px-2">
          {ENVS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setActiveEnv(e)}
              className={cn(
                "inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 h-9 px-2.5 text-sm text-muted-foreground border-b-2 border-transparent hover:text-foreground transition-colors capitalize",
                activeEnv === e && "text-foreground border-[rgb(var(--of-fg-default))] font-semibold",
              )}
            >
              {e}
              {state.envs[e].protectionLevel !== "none" && (
                <span className="ml-1 text-2xs uppercase tracking-wider font-mono bg-accent-subtle text-accent border border-accent/30 rounded px-1.5">
                  {state.envs[e].protectionLevel}
                </span>
              )}
            </button>
          ))}
        </nav>
        <EnvPanel
          key={activeEnv}
          orgSlug={org!}
          projectSlug={project!}
          env={activeEnv}
          state={envState}
          onSaved={() => qc.invalidateQueries({ queryKey: ["protection", org, project] })}
        />
      </div>

      <OptionsAllowlistCard
        orgSlug={org!}
        projectSlug={project!}
        current={state.optionsAllowlist}
        onSaved={() => qc.invalidateQueries({ queryKey: ["protection", org, project] })}
      />

      <FirewallSection orgSlug={org!} projectSlug={project!} />
    </div>
  );
}

function EnvPanel({
  orgSlug,
  projectSlug,
  env,
  state,
  onSaved,
}: {
  orgSlug: string;
  projectSlug: string;
  env: Env;
  state: EnvState;
  onSaved: () => void;
}) {
  const [level, setLevel] = useState<Level>(state.protectionLevel);
  const [password, setPassword] = useState("");
  const [trustedIps, setTrustedIps] = useState<string>(state.trustedIps.join("\n"));
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  // Reset local form state whenever the user switches to a different env
  // tab. We deliberately do NOT include `state.*` in deps — TanStack Query
  // hands us a fresh `state.trustedIps` array reference on every refetch,
  // and that would clobber the user's in-progress radio selection (caught
  // during live smoke: clicking the password radio bounced back to none
  // because a query refresh fired between click and render).
  useEffect(() => {
    setLevel(state.protectionLevel);
    setPassword("");
    setTrustedIps(state.trustedIps.join("\n"));
    setRevealedSecret(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { protectionLevel: level };
      if (password) body.password = password;
      if (level === "trusted_ips") {
        body.trustedIps = trustedIps
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return api.put(`/orgs/${orgSlug}/projects/${projectSlug}/protection/${env}`, body);
    },
    onSuccess: () => {
      setPassword("");
      onSaved();
    },
  });

  const regenerate = useMutation({
    mutationFn: () =>
      api.post<{ secret: string }>(
        `/orgs/${orgSlug}/projects/${projectSlug}/protection/${env}/bypass-secret/regenerate`,
      ),
    onSuccess: (r) => {
      setRevealedSecret(r.secret);
      onSaved();
    },
  });

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="space-y-1">
        <Label className="text-xs">Protection level</Label>
        <div role="radiogroup" className="space-y-1.5">
          {(
            [
              { v: "none", label: "None", help: "Anyone with the URL can view this env." },
              { v: "platform_auth", label: "Platform members", help: "Visitors sign in with their openfront account; anyone with access to this project gets through — no shared secrets." },
              { v: "password", label: "Password", help: "Visitors must enter a password to view this env." },
              { v: "trusted_ips", label: "Trusted IPs", help: "Only requests from listed CIDR ranges are allowed." },
            ] as Array<{ v: Level; label: string; help: string }>
          ).map((opt) => (
            <label key={opt.v} className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name={`level-${env}`}
                value={opt.v}
                checked={level === opt.v}
                onChange={() => setLevel(opt.v)}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{opt.label}</span>
                <span className="block text-2xs text-subtle-foreground">{opt.help}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {level === "password" && (
        <div className="space-y-1">
          <Label className="text-xs" htmlFor={`pw-${env}`}>
            {state.hasPassword ? "Rotate password" : "Set password"}
          </Label>
          <Input
            id={`pw-${env}`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={state.hasPassword ? "leave blank to keep current" : "at least 8 chars"}
            autoComplete="new-password"
          />
          {state.passwordSetAt && (
            <div className="text-2xs text-subtle-foreground">
              Current password set {new Date(state.passwordSetAt).toLocaleDateString()}.
            </div>
          )}
        </div>
      )}

      {level === "trusted_ips" && (
        <div className="space-y-1">
          <Label className="text-xs" htmlFor={`ips-${env}`}>Trusted CIDR ranges</Label>
          <textarea
            id={`ips-${env}`}
            value={trustedIps}
            onChange={(e) => setTrustedIps(e.target.value)}
            rows={4}
            placeholder={"203.0.113.0/24\n10.0.0.0/8\n2001:db8::/32"}
            className="w-full px-2 py-2 rounded-md border border-border bg-background text-xs font-mono"
          />
          <div className="text-2xs text-subtle-foreground">
            One CIDR or IP per line. Empty list blocks every request.
          </div>
        </div>
      )}

      {save.error instanceof ApiError && (
        <div className="text-xs text-danger">{save.error.message}</div>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="text-2xs text-subtle-foreground">
          {state.hasBypassSecret ? "Bypass secret is set." : "No bypass secret."}{" "}
          <button
            type="button"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
            className="inline-flex items-center gap-1 underline hover:text-foreground"
          >
            {regenerate.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCw className="h-3 w-3" />
            )}
            Regenerate
          </button>
        </div>
        <Button variant="primary" size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      {revealedSecret && (
        <CopyableSecret value={revealedSecret} onClose={() => setRevealedSecret(null)} />
      )}
    </div>
  );
}

function OptionsAllowlistCard({
  orgSlug,
  projectSlug,
  current,
  onSaved,
}: {
  orgSlug: string;
  projectSlug: string;
  current: string[];
  onSaved: () => void;
}) {
  const [text, setText] = useState(current.join("\n"));
  useEffect(() => {
    setText(current.join("\n"));
  }, [current]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/orgs/${orgSlug}/projects/${projectSlug}/protection/options-allowlist`, {
        paths: text
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5 text-muted-foreground" /> OPTIONS allowlist
        </h3>
      </div>
      <div className="px-4 py-3 space-y-2">
        <p className="text-xs text-muted-foreground">
          Path prefixes that bypass password/IP protection for CORS preflight (OPTIONS) requests
          only. Required for password-gated SPAs that do cross-origin XHR.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={"/api/\n/graphql"}
          className="w-full px-2 py-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <div className="text-2xs text-subtle-foreground">
          One path per line; each must start with <span className="font-mono">/</span>.
        </div>
        {save.error instanceof ApiError && (
          <div className="text-xs text-danger">{save.error.message}</div>
        )}
        <div className="flex justify-end">
          <Button variant="primary" size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save allowlist
          </Button>
        </div>
      </div>
    </div>
  );
}

function CopyableSecret({ value, onClose }: { value: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border border-success/30 bg-success-subtle/50 p-3 space-y-2">
      <div className="text-2xs font-semibold">Bypass secret (shown ONCE — copy now)</div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={value}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 min-w-0 h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            const ok = await copyText(value);
            if (ok) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
      <div className="text-2xs text-muted-foreground">
        Send as <span className="font-mono break-all">X-Openfront-Protection-Bypass: {value.slice(0, 12)}…</span>{" "}
        to bypass password/IP gates from CI.
      </div>
    </div>
  );
}

// Re-export `useMemo` import to silence isolatedModules; some helpers use it.
void useMemo;
